import { Simulate } from 'react-dom/test-utils';

import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { dropdownDriverFactory as coreDriverFactory } from '../internal/WixUiCoreComponents/components/CoreDropdown/CoreDropdown.driver';

import { DATA_HOOKS } from './constants';
import { DATA_HOOKS as CONTENT_DATA_HOOKS } from '../internal/WixUiCoreComponents/components/CoreDropdownContent/CoreDropdownContent';
import { KeyDefinitionType } from '@wix/unidriver-core';

export interface FloatingDropdownDriver extends BaseUniDriver {
  dropdownContentDisplayed(): Promise<boolean>;
  clickOnDropdownBase(): Promise<void>;
  selectOptionAt(i: number): Promise<void>;
  getBaseSelectedValue(): Promise<string>;
  getAriaExpanded(): Promise<string>;
  getAriaActivedescendant(): Promise<string>;
  getAriaLabel(): Promise<string>;
  keydown?: (key: KeyDefinitionType) => Promise<void>;
}

type LocalDriver = Omit<FloatingDropdownDriver, 'exists' | 'click' | 'element'>;

export const floatingDropdownDriverFactory = (
  base: UniDriver,
): FloatingDropdownDriver => {
  const baseUniDriver = baseUniDriverFactory(base);

  const isMobile = async () => base.$('[data-mobile=true]').exists();

  const getDropdownBase = async () =>
    base.$(selectorFromDataHook(DATA_HOOKS.base));

  const getDropdownFloatingButton = async () =>
    base.$(selectorFromDataHook(DATA_HOOKS.floatingDropdownButton));
  const getDropdownFloatingContent = async () =>
    base.$(selectorFromDataHook(CONTENT_DATA_HOOKS.optionsContainer));

  const getDropdownCoreDriver = async () => {
    const element = (await baseUniDriver.element()).querySelector(
      selectorFromDataHook(DATA_HOOKS.coreDropdown),
    );
    return coreDriverFactory({ element, eventTrigger: Simulate });
  };

  const dropdownContentDisplayed = async () =>
    (await getDropdownCoreDriver()).dropdownContentDisplayed();

  const defaultDriver = (): LocalDriver => ({
    clickOnDropdownBase: async () => (await getDropdownBase()).click(),
    selectOptionAt: async (index: number) =>
      (await getDropdownCoreDriver()).optionAt(index).click(),
    dropdownContentDisplayed,
    getBaseSelectedValue: async () =>
      (await getDropdownBase())
        .$(selectorFromDataHook(DATA_HOOKS.baseSelectedValue))
        .text(),
    getAriaExpanded: async () =>
      (await getDropdownFloatingButton()).attr('aria-expanded'),
    getAriaLabel: async () =>
      (await getDropdownFloatingButton()).attr('aria-label'),
    getAriaActivedescendant: async () =>
      (await getDropdownFloatingContent()).attr('aria-activedescendant'),
    keydown: async (key: KeyDefinitionType) => {
      await (await getDropdownFloatingButton()).pressKey(key);
    },
  });

  const getNativeProp = async (prop: string) => {
    return (await getDropdownBase())
      .$(selectorFromDataHook(DATA_HOOKS.nativeSelect))
      ._prop(prop);
  };

  const nativeDriver = (): LocalDriver => ({
    clickOnDropdownBase: async () => (await getDropdownBase()).click(),
    selectOptionAt: async () => {},
    dropdownContentDisplayed: async () => false,
    getBaseSelectedValue: async () => {
      const selectedIndex = await getNativeProp('selectedIndex');
      const options = await getNativeProp('options');
      return options[selectedIndex]?.text;
    },
    getAriaExpanded: async () =>
      (await getDropdownFloatingButton()).attr('aria-expanded'),
    getAriaLabel: async () =>
      (await getDropdownBase())
        .$(selectorFromDataHook(DATA_HOOKS.nativeSelect))
        .attr('aria-label'),
    getAriaActivedescendant: async () =>
      (await getDropdownFloatingButton()).attr('aria-activedescendant'),
  });

  const getDriver = async (): Promise<LocalDriver> =>
    (await isMobile()) ? nativeDriver() : defaultDriver();

  return {
    ...baseUniDriver,
    clickOnDropdownBase: async () => (await getDriver()).clickOnDropdownBase(),
    dropdownContentDisplayed: async () =>
      (await getDriver()).dropdownContentDisplayed(),
    selectOptionAt: async (index: number) =>
      (await getDriver()).selectOptionAt(index),
    getBaseSelectedValue: async () =>
      (await getDriver()).getBaseSelectedValue(),
    getAriaExpanded: async () => (await getDriver()).getAriaExpanded(),
    getAriaLabel: async () => (await getDriver()).getAriaLabel(),
    getAriaActivedescendant: async () =>
      (await getDriver()).getAriaActivedescendant(),
    keydown: async (key: KeyDefinitionType) => (await getDriver()).keydown(key),
  };
};

function selectorFromDataHook(dataHook) {
  return `[data-hook="${dataHook}"]`;
}
